// src/context/CartContext.jsx
import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';

const CartContext = createContext();
const CART_STORAGE_KEY = 'pontso_yarona_cart';
const WISHLIST_STORAGE_KEY = 'pontso_yarona_wishlist';

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload.items || [],
        wishlist: action.payload.wishlist || [],
        isLoaded: true
      };

    case 'ADD_TO_CART':
      const existingItemIndex = state.items.findIndex(item => item.id === action.payload.product.id);
      
      if (existingItemIndex >= 0) {
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + action.payload.quantity
        };
        return { ...state, items: updatedItems };
      } else {
        return {
          ...state,
          items: [...state.items, {
            id: action.payload.product.id,
            name: action.payload.product.name,
            price: action.payload.product.price,
            priceValue: action.payload.product.priceValue,
            image: action.payload.product.image,
            category: action.payload.product.category,
            type: action.payload.product.type,
            quantity: action.payload.quantity,
            addedAt: new Date().toISOString()
          }]
        };
      }

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload.productId)
      };

    case 'UPDATE_QUANTITY':
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.id !== action.payload.productId)
        };
      }
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.productId
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };

    case 'CLEAR_CART':
      return { ...state, items: [] };

    case 'ADD_TO_WISHLIST':
      if (state.wishlist.some(item => item.id === action.payload.product.id)) {
        return state; // Already in wishlist
      }
      return {
        ...state,
        wishlist: [...state.wishlist, {
          id: action.payload.product.id,
          name: action.payload.product.name,
          price: action.payload.product.price,
          priceValue: action.payload.product.priceValue,
          image: action.payload.product.image,
          category: action.payload.product.category,
          type: action.payload.product.type,
          addedAt: new Date().toISOString()
        }]
      };

    case 'REMOVE_FROM_WISHLIST':
      return {
        ...state,
        wishlist: state.wishlist.filter(item => item.id !== action.payload.productId)
      };

    case 'CLEAR_WISHLIST':
      return { ...state, wishlist: [] };

    case 'SET_CART_FROM_FIRESTORE':
      return {
        ...state,
        items: action.payload.items || [],
        wishlist: action.payload.wishlist || []
      };

    default:
      return state;
  }
};

// Initial state
const initialState = {
  items: [],
  wishlist: [],
  isLoaded: false
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user: currentUser, loading: authLoading } = useAuth();
  // Track the previous user uid to detect login/logout transitions
  const prevUidRef = useRef(null);

  // Load cart and wishlist from localStorage on mount (guest state)
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
      dispatch({
        type: 'LOAD_CART',
        payload: {
          items: savedCart ? JSON.parse(savedCart) : [],
          wishlist: savedWishlist ? JSON.parse(savedWishlist) : []
        }
      });
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      dispatch({ type: 'LOAD_CART', payload: { items: [], wishlist: [] } });
    }
  }, []);

  // React to auth changes: merge on login, clear Firestore ref on logout
  // This replaces both the old onAuthStateChanged and the redundant Firestore load effect.
  useEffect(() => {
    if (authLoading) return;

    const uid = currentUser?.uid || null;
    const prevUid = prevUidRef.current;

    // No change in user identity → nothing to do
    if (uid === prevUid) return;
    prevUidRef.current = uid;

    if (uid) {
      // User just logged in → merge localStorage cart with Firestore cart (once)
      const mergeAndLoad = async () => {
        try {
          const localCart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '[]');
          const localWishlist = JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');

          const cartRef = doc(db, 'users', uid, 'cart', 'current');
          const wishlistRef = doc(db, 'users', uid, 'wishlist', 'current');

          const [cartDoc, wishlistDoc] = await Promise.all([getDoc(cartRef), getDoc(wishlistRef)]);

          const firestoreCart = cartDoc.exists() ? cartDoc.data().items || [] : [];
          const firestoreWishlist = wishlistDoc.exists() ? wishlistDoc.data().items || [] : [];

          // Merge: Firestore items take precedence; local items are added on top
          const mergedCart = [...firestoreCart];
          localCart.forEach((localItem) => {
            const idx = mergedCart.findIndex(i => i.id === localItem.id);
            if (idx >= 0) {
              mergedCart[idx] = { ...mergedCart[idx], quantity: mergedCart[idx].quantity + localItem.quantity };
            } else {
              mergedCart.push(localItem);
            }
          });

          const mergedWishlist = [...firestoreWishlist];
          localWishlist.forEach((localItem) => {
            if (!mergedWishlist.some(i => i.id === localItem.id)) mergedWishlist.push(localItem);
          });

          // Persist merged state to Firestore
          await Promise.all([
            setDoc(cartRef, { items: mergedCart, updatedAt: serverTimestamp() }, { merge: true }),
            setDoc(wishlistRef, { items: mergedWishlist, updatedAt: serverTimestamp() }, { merge: true }),
          ]);

          dispatch({ type: 'SET_CART_FROM_FIRESTORE', payload: { items: mergedCart, wishlist: mergedWishlist } });

          // Remove guest localStorage entries now that Firestore owns the cart
          localStorage.removeItem(CART_STORAGE_KEY);
          localStorage.removeItem(WISHLIST_STORAGE_KEY);
        } catch (error) {
          console.error('Error merging cart with Firestore:', error);
        }
      };
      mergeAndLoad();
    }
    // On logout the cart state stays in memory; persistence effect will write it to localStorage.
  }, [currentUser, authLoading]);

  // Persist cart changes: Firestore when logged in, localStorage when guest
  useEffect(() => {
    if (!state.isLoaded) return;
    if (currentUser) {
      const cartRef = doc(db, 'users', currentUser.uid, 'cart', 'current');
      setDoc(cartRef, { items: state.items, updatedAt: serverTimestamp() }, { merge: true })
        .catch(err => console.error('Error saving cart to Firestore:', err));
    } else {
      try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.items)); }
      catch (err) { console.error('Error saving cart to localStorage:', err); }
    }
  }, [state.items, state.isLoaded, currentUser]);

  // Persist wishlist changes
  useEffect(() => {
    if (!state.isLoaded) return;
    if (currentUser) {
      const wishlistRef = doc(db, 'users', currentUser.uid, 'wishlist', 'current');
      setDoc(wishlistRef, { items: state.wishlist, updatedAt: serverTimestamp() }, { merge: true })
        .catch(err => console.error('Error saving wishlist to Firestore:', err));
    } else {
      try { localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(state.wishlist)); }
      catch (err) { console.error('Error saving wishlist to localStorage:', err); }
    }
  }, [state.wishlist, state.isLoaded, currentUser]);

  // Cart actions
  const addToCart = (product, quantity = 1) => {
    dispatch({
      type: 'ADD_TO_CART',
      payload: { product, quantity }
    });
  };

  const removeFromCart = (productId) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: { productId }
    });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { productId, quantity }
    });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  // Wishlist actions
  const addToWishlist = (product) => {
    dispatch({
      type: 'ADD_TO_WISHLIST',
      payload: { product }
    });
  };

  const removeFromWishlist = (productId) => {
    dispatch({
      type: 'REMOVE_FROM_WISHLIST',
      payload: { productId }
    });
  };

  const toggleWishlist = (product) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const isInWishlist = (productId) => {
    return state.wishlist.some(item => item.id === productId);
  };

  const clearWishlist = () => {
    dispatch({ type: 'CLEAR_WISHLIST' });
  };

  // Calculate cart summary
  const cartSummary = {
    itemCount: state.items.reduce((total, item) => total + item.quantity, 0),
    uniqueItemCount: state.items.length,
    subtotal: state.items.reduce((total, item) => total + (item.priceValue * item.quantity), 0),
    get total() {
      // Add any taxes, shipping, etc. here
      return this.subtotal;
    },
    get formattedSubtotal() {
      return `R${this.subtotal.toFixed(2)}`;
    },
    get formattedTotal() {
      return `R${this.total.toFixed(2)}`;
    }
  };

  const value = {
    // State
    cartItems: state.items,
    wishlistItems: state.wishlist,
    cartSummary,
    isLoaded: state.isLoaded,
    
    // Cart actions
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isInCart,
    getItemQuantity,
    
    // Wishlist actions
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    clearWishlist,
    wishlistCount: state.wishlist.length
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const useWishlist = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useWishlist must be used within a CartProvider');
  }
  
  return {
    wishlistItems: context.wishlistItems,
    addToWishlist: context.addToWishlist,
    removeFromWishlist: context.removeFromWishlist,
    toggleWishlist: context.toggleWishlist,
    isInWishlist: context.isInWishlist,
    clearWishlist: context.clearWishlist,
    wishlistCount: context.wishlistCount,
    isLoading: !context.isLoaded
  };
};